// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Hooks to fetch dataset definitions — the whole workspace (every agent's
 * datasets, tagged with agentId) or a single agent's.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useServices } from '@/app-shell/services'
import { listAgentDatasets, listDatasets, updateDatasetUserSections } from '../services/dataset.service'
import type { DatasetRecord } from '../types'

export const datasetKeys = {
  list: (orgId: string, wsId: string) => ['datasets', orgId, wsId] as const,
  agentList: (orgId: string, wsId: string, agentId: string) =>
    ['datasets', orgId, wsId, agentId] as const,
}

/** All datasets in a workspace, across every agent. */
export const useDatasets = (orgId?: string, wsId?: string) => {
  const services = useServices()
  const apiClient = services.getClient()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: datasetKeys.list(orgId ?? '', wsId ?? ''),
    queryFn: () => listDatasets(apiClient, orgId!, wsId!),
    enabled: !!orgId && !!wsId,
    staleTime: 5 * 60_000,
  })

  const refresh = useMemo(() => async () => {
    if (!orgId || !wsId) return
    await queryClient.invalidateQueries({
      queryKey: datasetKeys.list(orgId, wsId),
    })
  }, [orgId, wsId, queryClient])

  return {
    datasets: data ?? ([] as DatasetRecord[]),
    loading: isLoading,
    error: error ? (error instanceof Error ? error : new Error('Unknown error')) : null,
    refresh,
  }
}

/** Updates only the user-editable section values of an agent's dataset (workspace admins). */
export const useUpdateDatasetUserSections = (orgId: string, wsId: string, agentId: string) => {
  const services = useServices()
  const apiClient = services.getClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ datasetKey, userSections }: { datasetKey: string; userSections: Record<string, string> }) =>
      updateDatasetUserSections(apiClient, orgId, wsId, agentId, datasetKey, userSections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: datasetKeys.agentList(orgId, wsId, agentId) })
      queryClient.invalidateQueries({ queryKey: datasetKeys.list(orgId, wsId) })
    },
  })
}

/** The datasets owned by a single agent. */
export const useAgentDatasets = (orgId?: string, wsId?: string, agentId?: string) => {
  const services = useServices()
  const apiClient = services.getClient()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: datasetKeys.agentList(orgId ?? '', wsId ?? '', agentId ?? ''),
    queryFn: () => listAgentDatasets(apiClient, orgId!, wsId!, agentId!),
    enabled: !!orgId && !!wsId && !!agentId,
    staleTime: 5 * 60_000,
  })

  const refresh = useMemo(() => async () => {
    if (!orgId || !wsId || !agentId) return
    await queryClient.invalidateQueries({
      queryKey: datasetKeys.agentList(orgId, wsId, agentId),
    })
  }, [orgId, wsId, agentId, queryClient])

  return {
    datasets: data ?? ([] as DatasetRecord[]),
    loading: isLoading,
    error: error ? (error instanceof Error ? error : new Error('Unknown error')) : null,
    refresh,
  }
}
